// src/config/SeoMapping.js
import { serviceSEO, pageSEO, defaultSEO, siteConfig } from '../utils/SeoConfig';

// Main function to get SEO data based on path
export const getSEOForPath = (path) => {
  // Remove trailing slash
  const cleanPath = path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;
  
  // Check if it's a service page
  if (cleanPath.startsWith('/services/')) {
    const serviceSlug = cleanPath.replace('/services/', '');
    return serviceSEO[serviceSlug] || getDefaultServiceSEO(serviceSlug);
  }
  
  // Check if it's a regular page
  return pageSEO[cleanPath] || defaultSEO;
};

// Get default SEO for unknown service slugs
export const getDefaultServiceSEO = (slug) => {
  const formattedSlug = slug.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
  
  return {
    title: `${formattedSlug} in Nashik | Deccan Services`,
    description: `Professional ${formattedSlug.toLowerCase()} services in Nashik. Expert technicians, same-day service, genuine parts, 90-day warranty. Book your appointment today!`,
    keywords: `${slug.replace(/-/g, ' ')}, ${slug.replace(/-/g, ' ')} Nashik, appliance repair, Deccan Services`,
    heading: `Professional ${formattedSlug} Services`,
    subheading: `Expert ${formattedSlug.toLowerCase()} by certified technicians`,
    image: `/images/services/default-service.jpg`,
    canonical: `/services/${slug}`
  };
};

// Generate structured data for LocalBusiness
export const getLocalBusinessStructuredData = () => {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": siteConfig.siteName,
    "alternateName": "Deccan Services - Apna Service Partner",
    "description": defaultSEO.description,
    "url": siteConfig.siteUrl,
    "logo": siteConfig.siteLogo,
    "image": siteConfig.defaultImage,
    "telephone": siteConfig.phones[0],
    "email": siteConfig.email,
    "priceRange": "₹₹",
    "address": [
      {
        "@type": "PostalAddress",
        "streetAddress": siteConfig.address.nashik.street,
        "addressLocality": siteConfig.address.nashik.city,
        "addressRegion": siteConfig.address.nashik.state,
        "postalCode": siteConfig.address.nashik.pincode,
        "addressCountry": siteConfig.address.nashik.country
      },
      {
        "@type": "PostalAddress",
        "streetAddress": siteConfig.address.vadodara.street,
        "addressLocality": siteConfig.address.vadodara.city,
        "addressRegion": siteConfig.address.vadodara.state,
        "addressCountry": siteConfig.address.vadodara.country
      }
    ],
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    },
    "sameAs": Object.values(siteConfig.socialMedia),
    "foundingDate": siteConfig.founded,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": siteConfig.rating.value,
      "reviewCount": siteConfig.rating.count
    }
  };
};

// Generate structured data for Service
export const getServiceStructuredData = (serviceData, slug) => {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": serviceData.heading || serviceData.title,
    "description": serviceData.description || serviceData.subheading,
    "provider": {
      "@type": "LocalBusiness",
      "name": siteConfig.siteName,
      "url": siteConfig.siteUrl
    },
    "areaServed": {
      "@type": "City",
      "name": "Nashik"
    },
    "serviceType": slug.replace(/-/g, ' '),
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "priceCurrency": "INR"
      }
    }
  };
};

// Generate breadcrumb structured data
export const getBreadcrumbStructuredData = (path) => {
  const paths = path.split('/').filter(p => p);
  let breadcrumbs = [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": siteConfig.siteUrl
    }
  ];

  let currentPath = '';
  paths.forEach((segment, index) => {
    currentPath += `/${segment}`;
    let name = segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    
    // Special handling for service pages
    if (segment === 'services' && paths[index + 1]) {
      // Services page will be added separately
      return;
    }
    
    breadcrumbs.push({
      "@type": "ListItem",
      "position": index + 2,
      "name": name,
      "item": `${siteConfig.siteUrl}${currentPath}`
    });
  });

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs
  };
};

// Get all meta tags for a specific path
export const getMetaTags = (path) => {
  const seo = getSEOForPath(path);
  const isServicePage = path.startsWith('/services/');
  
  return {
    title: seo.title,
    meta: [
      { name: "description", content: seo.description },
      { name: "keywords", content: seo.keywords },
      { name: "author", content: seo.author || defaultSEO.author },
      { name: "robots", content: seo.robots || defaultSEO.robots },
      { name: "language", content: seo.language || defaultSEO.language },
      { name: "revisit-after", content: seo.revisitAfter || defaultSEO.revisitAfter },
      
      // Open Graph
      { property: "og:title", content: seo.title },
      { property: "og:description", content: seo.description },
      { property: "og:image", content: seo.image || defaultSEO.image },
      { property: "og:url", content: `${siteConfig.siteUrl}${path}` },
      { property: "og:type", content: isServicePage ? "website" : "website" },
      { property: "og:site_name", content: siteConfig.siteName },
      
      // Twitter
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: seo.title },
      { name: "twitter:description", content: seo.description },
      { name: "twitter:image", content: seo.image || defaultSEO.image },
    ],
    link: [
      { rel: "canonical", href: `${siteConfig.siteUrl}${seo.canonical || path}` }
    ]
  };
};

// Export all configurations
export default {
  siteConfig,
  defaultSEO,
  serviceSEO,
  pageSEO,
  getSEOForPath,
  getDefaultServiceSEO,
  getLocalBusinessStructuredData,
  getServiceStructuredData,
  getBreadcrumbStructuredData,
  getMetaTags
};